import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Clock, MapPin, User, Share2 } from 'lucide-react';
import { useMediaLightbox, LongPressMediaTile } from '../../components/mp/MediaLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const isVideo = (url) => url && ['mp4','webm','ogg','mov','avi','mkv'].includes(url.split('?')[0].split('.').pop().toLowerCase());
const resolveUrl = (url) => url?.startsWith('/') ? `${BACKEND_URL}${url}` : url;
const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

const platformColors = { bbc: '#BB1919', cnn: '#CC0000', reuters: '#FF8000', aljazeera: '#D4AA00', nytimes: '#000', guardian: '#052962', foxnews: '#003366', rt: '#5EAA22', xinhua: '#D52B1E' };

const MPArticleDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPost = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/mp/guest/article/${postId}`);
        setPost(res.data);
      } catch { setError('Article not found'); }
      finally { setLoading(false); }
    };
    loadPost();
  }, [postId]);

  // V14.13.9 / V14.13.11.1 — Hook MUST run on every render (Rules of Hooks).
  // Previously placed AFTER the early returns below, which broke the prod
  // CRA build with `react-hooks/rules-of-hooks`. Use optional chaining on
  // `post` since the hook now runs before we know if the article loaded.
  const allMedia = post?.media_urls || [];
  const { open, Lightbox } = useMediaLightbox();

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>;
  if (error || !post) return <div className="min-h-screen flex flex-col items-center justify-center bg-background"><p className="text-muted-foreground mb-4">{error}</p><Button onClick={() => navigate(-1)}>Go Back</Button></div>;

  const color = platformColors[post.platform] || '#333';

  return (
    <div className="min-h-screen bg-background" data-testid="mp-article-detail">
      {Lightbox}
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-4xl mx-auto flex h-14 items-center gap-4 px-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="article-back-btn"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
          <div className="flex items-center gap-2 ml-auto">
            <div className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: color }}>{post.platform?.charAt(0).toUpperCase()}</div>
            <span className="text-sm font-semibold uppercase" style={{ color }}>{post.platform}</span>
          </div>
        </div>
      </header>
      <article className="max-w-4xl mx-auto px-4 py-8">
        {post.category && <Badge variant="outline" className="uppercase text-xs mb-4" style={{ borderColor: color, color }}>{post.category}</Badge>}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6" data-testid="article-title">{post.title || post.content?.substring(0, 80)}</h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8 pb-6 border-b border-border">
          {post.author?.name && <div className="flex items-center gap-1"><User className="w-4 h-4" /><span>By <strong>{post.author.name}</strong></span></div>}
          <div className="flex items-center gap-1"><Clock className="w-4 h-4" /><span>{formatDate(post.timestamp)}</span></div>
          {post.location && <div className="flex items-center gap-1"><MapPin className="w-4 h-4" /><span>{post.location}</span></div>}
        </div>
        {allMedia.length > 0 && (
          <LongPressMediaTile index={0} onActivate={(i) => open(allMedia, i)} className="mb-8 rounded-xl overflow-hidden block">
            {isVideo(allMedia[0])
              ? <video src={resolveUrl(allMedia[0])} className="w-full max-h-[500px] object-cover" controls playsInline />
              : <img src={resolveUrl(allMedia[0])} alt="" className="w-full max-h-[500px] object-cover" />}
          </LongPressMediaTile>
        )}
        <div className="prose prose-lg dark:prose-invert max-w-none">
          {post.content?.split('\n').map((p, i) => p.trim() ? <p key={i} className="text-foreground leading-relaxed mb-4">{p}</p> : null)}
        </div>
        {allMedia.length > 1 && (
          <div className="mt-8 grid grid-cols-2 gap-4">
            {allMedia.slice(1).map((url, i) => (
              <LongPressMediaTile key={i} index={i + 1} onActivate={(idx) => open(allMedia, idx)} className="rounded-lg overflow-hidden block">
                {isVideo(url)
                  ? <video src={resolveUrl(url)} className="w-full h-48 object-cover" controls playsInline />
                  : <img src={resolveUrl(url)} alt="" className="w-full h-48 object-cover" />}
              </LongPressMediaTile>
            ))}
          </div>
        )}
      </article>
    </div>
  );
};

export default MPArticleDetailPage;
